import { useState, useEffect, useRef } from 'react';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { H4 } from 'foundations-components/transitional/components/H4';
import { H7 } from 'foundations-components/transitional/components/H7';
import { HR } from 'foundations-components/transitional/components/HR';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { Form } from 'foundations-components/transitional/components/Form';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { ViewIcon } from 'foundations-assets/icons/components/ViewIcon';
import { HideIcon } from 'foundations-assets/icons/components/HideIcon';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import { ChirpError } from 'rpc-client-utils';
import { useCreateOrUpdatePasswordMutation } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/SettingsRpc.hooks';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ERROR_ALERT_ID = 'password-reset-error';
const PANEL_WIDTH_BY_BREAKPOINT = {
  mobile: '100%',
  tablet: '80%',
  desktop: 600
};
export function PasswordResetPanel({
  hasPassword,
  onClose,
  onBack
}) {
  const breakpoint = useBreakpoint();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const previouslyFocusedRef = useRef(document.activeElement);
  useEffect(() => {
    const trigger = previouslyFocusedRef.current;
    return () => {
      requestAnimationFrame(() => {
        trigger === null || trigger === void 0 || trigger.focus();
      });
    };
  }, []);
  const [alertStore] = useState(() => FloatingAlertStore.newFloatingAlertStore());
  const accountId = getPortalAccountId();
  const portalInstanceId = getPortalInstanceId();
  const [createOrUpdatePassword, {
    loading
  }] = useCreateOrUpdatePasswordMutation();
  const handleError = error => {
    var _error$networkError$c, _error$networkError, _settingsError$passwo;
    const rpcError = (_error$networkError$c = error === null || error === void 0 || (_error$networkError = error.networkError) === null || _error$networkError === void 0 ? void 0 : _error$networkError.cause) !== null && _error$networkError$c !== void 0 ? _error$networkError$c : error === null || error === void 0 ? void 0 : error.cause;
    const settingsError = rpcError instanceof ChirpError ? rpcError : undefined;
    const passwordErrors = (_settingsError$passwo = settingsError === null || settingsError === void 0 ? void 0 : settingsError.passwordErrors) !== null && _settingsError$passwo !== void 0 ? _settingsError$passwo : [];
    switch (settingsError === null || settingsError === void 0 ? void 0 : settingsError.reason) {
      case 'WRONG_CURRENT_PASSWORD':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorWrongCurrentPassword')
        });
        break;
      case 'INVALID_PASSWORD':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: passwordErrors.length ? passwordErrors.join(' ') : I18n.text('app.myAccountPopover.settings.security.errorInvalidPassword')
        });
        break;
      case 'PASSWORD_LEAKED':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorPasswordLeaked')
        });
        break;
      case 'SAME_PASSWORD':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorSamePassword')
        });
        break;
      case 'PASSWORDS_DO_NOT_MATCH':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorPasswordsDoNotMatch')
        });
        break;
      case 'RATE_LIMITED':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorRateLimited')
        });
        break;
      default:
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorGeneric')
        });
    }
  };
  const handleSubmit = event => {
    event.preventDefault();
    alertStore.removeAlert(ERROR_ALERT_ID);
    if (hasPassword && !currentPassword || !newPassword || !confirmPassword) {
      alertStore.addAlert({
        id: ERROR_ALERT_ID,
        type: 'danger',
        titleText: I18n.text('app.myAccountPopover.settings.security.errorRequired')
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      alertStore.addAlert({
        id: ERROR_ALERT_ID,
        type: 'danger',
        titleText: I18n.text('app.myAccountPopover.settings.security.errorPasswordsDoNotMatch')
      });
      return;
    }
    if (!accountId || !portalInstanceId) {
      alertStore.addAlert({
        id: ERROR_ALERT_ID,
        type: 'danger',
        titleText: I18n.text('app.myAccountPopover.settings.security.errorGeneric')
      });
      return;
    }
    createOrUpdatePassword({
      variables: Object.assign({}, hasPassword ? {
        currentPassword
      } : {}, {
        newPassword,
        newPasswordConfirm: confirmPassword,
        accountId,
        portalInstanceId
      })
    }).then(() => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      alertStore.addAlert({
        type: 'success',
        titleText: I18n.text(hasPassword ? 'app.myAccountPopover.settings.security.changeSuccess' : 'app.myAccountPopover.settings.security.setSuccess'),
        dismissAutoMs: 5000
      });
    }).catch(handleError);
  };
  return /*#__PURE__*/_jsxs(Panel, {
    width: PANEL_WIDTH_BY_BREAKPOINT[breakpoint],
    children: [/*#__PURE__*/_jsx(CloseButton, {
      onClick: onClose,
      color: "text-core-default"
    }), /*#__PURE__*/_jsx(PanelBody, {
      children: /*#__PURE__*/_jsxs(PanelSection, {
        children: [/*#__PURE__*/_jsx(FloatingAlertList, {
          alertStore: alertStore,
          use: "contextual"
        }), /*#__PURE__*/_jsx(Button, {
          use: "unstyled",
          onClick: onBack,
          paddingInline: "0",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "100",
            inlineSize: "100%",
            justifyContent: "start",
            children: [/*#__PURE__*/_jsx(LeftIcon, {
              size: "100",
              color: "text-core-default"
            }), /*#__PURE__*/_jsx(Paragraph, {
              color: "text-core-default",
              marginBlock: "0",
              typography: "body-100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.security.back"
              })
            })]
          })
        }), /*#__PURE__*/_jsx(H4, {
          color: "text-core-default",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: hasPassword ? 'app.myAccountPopover.settings.security.changePasswordHeading' : 'app.myAccountPopover.settings.security.setPasswordHeading'
          })
        }), /*#__PURE__*/_jsx(H7, {
          color: "text-core-default",
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: hasPassword ? 'app.myAccountPopover.settings.security.changePasswordSubtitle' : 'app.myAccountPopover.settings.security.setPasswordSubtitle'
          })
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsxs(Form, {
          onSubmit: handleSubmit,
          children: [/*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            marginBlockStart: "300",
            children: [hasPassword && /*#__PURE__*/_jsx(View, {
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(Fieldset, {
                _size: "small",
                children: /*#__PURE__*/_jsx(FormControl, {
                  label: unescapedText('app.myAccountPopover.settings.security.currentPasswordLabel'),
                  children: /*#__PURE__*/_jsxs(Flex, {
                    position: "relative",
                    flexDirection: "column",
                    children: [/*#__PURE__*/_jsx(TextInput, {
                      type: showCurrentPassword ? 'text' : 'password',
                      name: "currentPassword",
                      "data-test-id": "current-password-input",
                      value: currentPassword,
                      onChange: event => setCurrentPassword(event.target.value),
                      typography: "body-100",
                      paddingInlineEnd: "700"
                    }), /*#__PURE__*/_jsx(Flex, {
                      position: "absolute",
                      insetInlineEnd: "0",
                      insetBlockStart: "0",
                      insetBlockEnd: "0",
                      alignItems: "center",
                      children: /*#__PURE__*/_jsx(IconButton, {
                        use: "transparent",
                        shape: "square",
                        size: "sm",
                        type: "button",
                        "aria-label": showCurrentPassword ? I18n.text('app.myAccountPopover.settings.security.hidePassword') : I18n.text('app.myAccountPopover.settings.security.showPassword'),
                        tooltip: showCurrentPassword ? I18n.text('app.myAccountPopover.settings.security.hidePassword') : I18n.text('app.myAccountPopover.settings.security.showPassword'),
                        onClick: () => setShowCurrentPassword(v => !v),
                        children: showCurrentPassword ? /*#__PURE__*/_jsx(HideIcon, {}) : /*#__PURE__*/_jsx(ViewIcon, {})
                      })
                    })]
                  })
                })
              })
            }), /*#__PURE__*/_jsx(View, {
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(Fieldset, {
                _size: "small",
                children: /*#__PURE__*/_jsx(FormControl, {
                  label: unescapedText('app.myAccountPopover.settings.security.newPasswordLabel'),
                  children: /*#__PURE__*/_jsxs(Flex, {
                    position: "relative",
                    flexDirection: "column",
                    children: [/*#__PURE__*/_jsx(TextInput, {
                      type: showNewPassword ? 'text' : 'password',
                      name: "newPassword",
                      "data-test-id": "new-password-input",
                      value: newPassword,
                      onChange: event => setNewPassword(event.target.value),
                      typography: "body-100",
                      paddingInlineEnd: "700"
                    }), /*#__PURE__*/_jsx(Flex, {
                      position: "absolute",
                      insetInlineEnd: "0",
                      insetBlockStart: "0",
                      insetBlockEnd: "0",
                      alignItems: "center",
                      children: /*#__PURE__*/_jsx(IconButton, {
                        use: "transparent",
                        shape: "square",
                        size: "sm",
                        type: "button",
                        "aria-label": showNewPassword ? I18n.text('app.myAccountPopover.settings.security.hidePassword') : I18n.text('app.myAccountPopover.settings.security.showPassword'),
                        tooltip: showNewPassword ? I18n.text('app.myAccountPopover.settings.security.hidePassword') : I18n.text('app.myAccountPopover.settings.security.showPassword'),
                        onClick: () => setShowNewPassword(v => !v),
                        children: showNewPassword ? /*#__PURE__*/_jsx(HideIcon, {}) : /*#__PURE__*/_jsx(ViewIcon, {})
                      })
                    })]
                  })
                })
              })
            }), /*#__PURE__*/_jsx(View, {
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(Fieldset, {
                _size: "small",
                children: /*#__PURE__*/_jsx(FormControl, {
                  label: unescapedText('app.myAccountPopover.settings.security.confirmPasswordLabel'),
                  children: /*#__PURE__*/_jsxs(Flex, {
                    position: "relative",
                    flexDirection: "column",
                    children: [/*#__PURE__*/_jsx(TextInput, {
                      type: showConfirmPassword ? 'text' : 'password',
                      name: "confirmPassword",
                      "data-test-id": "confirm-password-input",
                      value: confirmPassword,
                      onChange: event => setConfirmPassword(event.target.value),
                      typography: "body-100",
                      paddingInlineEnd: "700"
                    }), /*#__PURE__*/_jsx(Flex, {
                      position: "absolute",
                      insetInlineEnd: "0",
                      insetBlockStart: "0",
                      insetBlockEnd: "0",
                      alignItems: "center",
                      children: /*#__PURE__*/_jsx(IconButton, {
                        use: "transparent",
                        shape: "square",
                        size: "sm",
                        type: "button",
                        "aria-label": showConfirmPassword ? I18n.text('app.myAccountPopover.settings.security.hidePassword') : I18n.text('app.myAccountPopover.settings.security.showPassword'),
                        tooltip: showConfirmPassword ? I18n.text('app.myAccountPopover.settings.security.hidePassword') : I18n.text('app.myAccountPopover.settings.security.showPassword'),
                        onClick: () => setShowConfirmPassword(v => !v),
                        children: showConfirmPassword ? /*#__PURE__*/_jsx(HideIcon, {}) : /*#__PURE__*/_jsx(ViewIcon, {})
                      })
                    })]
                  })
                })
              })
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            justifyContent: "end",
            gap: "200",
            marginBlockStart: "300",
            children: [/*#__PURE__*/_jsx(Button, {
              use: "secondary-ghost",
              type: "button",
              onClick: onBack,
              children: /*#__PURE__*/_jsx(Paragraph, {
                color: "text-core-default",
                marginBlock: "0",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "app.myAccountPopover.settings.security.cancel"
                })
              })
            }), /*#__PURE__*/_jsx(LoadingButton, {
              use: "primary",
              type: "submit",
              loading: loading,
              disabled: loading,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: hasPassword ? 'app.myAccountPopover.settings.security.updatePassword' : 'app.myAccountPopover.settings.security.setPasswordButton'
              })
            })]
          })]
        })]
      })
    })]
  });
}